import React, { useState, useEffect } from 'react';
import { ref, onValue } from '../lib/firebase-database';
import { auth, db } from '../firebase';
import { useAuth } from '../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { AdminLayout } from './AdminLayout';
import { Loader2, Mail, ShieldCheck, UserCog } from 'lucide-react';

export default function AdminPage() {
  const [admins, setAdmins] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!user || !isAdmin) {
      navigate('/');
      return;
    }

    setIsLoading(true);
    setError(null);
    const usersRef = ref(db, 'users');
    onValue(
      usersRef,
      (snapshot) => {
        const data = snapshot.val();
        if (data) {
          const adminsList = Object.entries(data)
            .map(([id, userData]) => ({ id, ...userData }))
            .filter((u) => u.isAdmin);
          setAdmins(adminsList);
        } else {
          setAdmins([]);
        }
        setIsLoading(false);
      },
      (err) => {
        console.error('Error fetching admins:', err);
        setError(`Failed to load admins: ${err.message}`);
        setIsLoading(false);
      }
    );
  }, [user, isAdmin, navigate]);

  const handleLogout = () => {
    auth.signOut().then(() => navigate('/login'));
  };

  return (
    <AdminLayout user={user} onLogout={handleLogout}>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Admins</h1>
            <p className="text-sm text-gray-500 mt-1">
              People with access to the admin panel.
            </p>
          </div>
          <button
            type="button"
            onClick={() => navigate('/add-admin')}
            className="inline-flex items-center justify-center px-4 py-2 bg-black text-white text-sm font-medium rounded-md hover:bg-gray-900"
          >
            <UserCog className="h-4 w-4 mr-2" /> Add Account
          </button>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-md px-4 py-3">
            {error}
          </div>
        )}

        {isLoading ? (
          <div className="flex items-center justify-center py-16 text-gray-500">
            <Loader2 className="h-5 w-5 animate-spin mr-2" />
            Loading admins...
          </div>
        ) : admins.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-lg py-16 text-center text-sm text-gray-500">
            No admins yet.
          </div>
        ) : (
          <div className="grid gap-3 grid-cols-1 md:grid-cols-2">
            {admins.map((admin) => (
              <div
                key={admin.id}
                className="bg-white border border-gray-200 rounded-lg p-4 flex items-center gap-4"
              >
                <img
                  src={admin.photoURL || '/placeholder.svg'}
                  alt={admin.displayName || 'Admin'}
                  className="w-12 h-12 rounded-full object-cover bg-gray-100"
                />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <h2 className="text-sm font-semibold text-gray-900 truncate">
                      {admin.displayName || admin.username || 'Admin'}
                    </h2>
                    <span className="inline-flex items-center gap-1 text-[11px] font-medium px-1.5 py-0.5 rounded bg-gray-900 text-white">
                      <ShieldCheck className="h-3 w-3" /> Admin
                    </span>
                  </div>
                  {admin.email && (
                    <p className="mt-1 flex items-center gap-1.5 text-xs text-gray-500 truncate">
                      <Mail className="h-3 w-3" /> {admin.email}
                    </p>
                  )}
                  <p className="mt-1 text-[11px] text-gray-400 truncate">
                    ID: {admin.id}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
